import { Link } from "react-router-dom";
import { brand } from "@/content/site";
import { industries } from "@/content/industries";
import { campaigns } from "@/content/campaigns";
import { Mail } from "lucide-react";

const year = new Date().getFullYear();

const serviceLinks = [
  { label: "Solutions", to: "/solutions" },
  { label: "Plans", to: "/plans" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Industries", to: "/industries" },
  { label: "Energy Growth Pilot", to: "/energy-growth-pilot" },
  { label: "Installer Growth Pilot", to: "/for/installer-growth-pilot" },
  { label: "Results", to: "/results" },
  { label: "Insights", to: "/insights" },
];

const companyLinks = [
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
  { label: "Privacy", to: "/privacy" },
  { label: "Terms", to: "/terms" },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-forest text-white overflow-hidden"
    >
      {/* decorative ring */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -bottom-40 h-[520px] w-[520px] rounded-full border-[3px] border-dashed border-white/5"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-10">
          <div className="col-span-2 lg:col-span-2 max-w-sm">
            <img
              src={brand.logo}
              alt="Green Circle Group logo"
              className="h-10 w-auto bg-white rounded-xl p-2"
            />
            <p className="mt-5 text-white/70 leading-relaxed">
              Green Circle Group helps busy Australian trade and home-service
              businesses get found, generate more enquiries and turn more
              opportunities into booked work—with a connected digital growth
              system working in the background.
            </p>
            <a
              href={`mailto:${brand.email}`}
              data-testid="footer-email"
              className="mt-6 inline-flex items-center gap-2 text-mint font-semibold hover:text-lime transition-colors"
            >
              <Mail className="h-4 w-4" />
              {brand.email}
            </a>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-white/50">
              Services
            </h3>
            <ul className="mt-5 space-y-3">
              {serviceLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-white/75 hover:text-lime transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-white/50">
              For Tradies
            </h3>
            <ul className="mt-5 space-y-3">
              <li>
                <Link
                  to="/for"
                  className="text-white/75 hover:text-lime transition-colors"
                >
                  All programs
                </Link>
              </li>
              {campaigns.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/for/${c.slug}`}
                    data-testid={`footer-campaign-${c.slug}`}
                    className="text-white/75 hover:text-lime transition-colors"
                  >
                    {c.navLabel}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-white/50">
              Industries
            </h3>
            <ul className="mt-5 space-y-3">
              {industries.map((i) => (
                <li key={i.slug}>
                  <Link
                    to={`/industries/${i.slug}`}
                    className="text-white/75 hover:text-lime transition-colors"
                  >
                    {i.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-white/50">
              Company
            </h3>
            <ul className="mt-5 space-y-3">
              {companyLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-white/75 hover:text-lime transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-sm text-white/55">
          <p data-testid="footer-copyright">
            © {year} Green Circle Group. All rights reserved.
          </p>
          <p>{brand.name}</p>
        </div>
        <p className="mt-4 text-xs text-white/40 max-w-2xl">
          {brand.owner} (ABN {brand.abn}).
        </p>
      </div>
    </footer>
  );
}
